// R-131-FIN-DECISION-F — orchestrates pathway picker, compare card, charts, and persistence.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
// aurora-qa-3 (⑩) — Pin/PinOff/Save/Sparkles/RotateCcw icons dropped with the
// save-comparison row + intent-defaults banner. Loader2 remains (loading spinner).
import { Link2, Loader2, Pin, PinOff, Save } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import { useUrlSearchParamSync } from '../../../lib/useUrlSearchParamSync';
import {
  pathwaysFromIntentPhases,
  recommendedPathwaysForIntent,
  feasibleTechnologiesForIntent,
  type FeasibleTechnologyVerdict,
} from '../../../lib/finDecision/intentPosture';
import { PRESET_BY_TECHNOLOGY } from '../../../lib/finDecision/presets';
import type { TechnologyId } from '../../../lib/finDecision/types';
import { pathwayDisplayNameWithCod } from '../../../lib/finDecision/pathwayQuickEdit';
import {
  listSavedSitePathwaysFromFirestore,
  loadPathwayComparisonFromFirestore,
  pinPathwayComparisonForSite,
  buildPathwayComparisonShareUrl,
  savePathwayComparisonToFirestore,
  saveSitePathwayToFirestore,
  unpinPathwayComparisonForSite,
} from '../../../services/pathwayComparisonFirestore';
import type { Pathway } from '../../../lib/finDecision/types';
import type { FinancialAssumptions } from '../../../types/financialAssumptions';
import type { ProjectIntent } from '../../../types/siteRag';
import type { SiteContext } from '../../../types/siteContext';
import type {
  PathwayComparisonDocument,
  SavedSitePathwayDocument,
} from '../../../types/pathwayComparison';
import { PathwayPicker } from './PathwayPicker';
import { PathwayCompareCard } from './PathwayCompareCard';
import { PathwayCashflowChart } from './PathwayCashflowChart';
import { PathwayLifecycleTimeline } from './PathwayLifecycleTimeline';
import { PathwayRiskMatrix } from './PathwayRiskMatrix';

export interface PathwayComparisonSectionProps {
  siteId: string;
  intent: ProjectIntent;
  siteContext: SiteContext;
  updatedBy: string;
  uid: string;
  financialAssumptions?: FinancialAssumptions | null;
  /** Initial comparison id from ?pathwayCompare= URL param. */
  initialComparisonId?: string | null;
  /** Site-level pinned comparison id (R-131-FIN-DECISION-K). */
  pinnedPathwayComparisonId?: string | null;
  onPinnedComparisonChange?: (comparisonId: string | null) => void;
  /**
   * aurora-qa-3 (⑪) — which slice of the pathway surface to render, so the
   * Financial tab can split it across sub-tabs while keeping ONE source of the
   * `pathways` state:
   *   - 'all' (default)           → picker + compare card + cashflow/lifecycle
   *                                 charts + risk matrix (the original layout;
   *                                 preserves existing callers + tests).
   *   - 'pathways'                → picker + compare card + risk matrix
   *                                 (NO cashflow/lifecycle charts).
   *   - 'cashflow-lifecycle'      → ONLY the cashflow + lifecycle charts.
   */
  view?: 'all' | 'pathways' | 'cashflow-lifecycle';
}

function clonePathways(pathways: Pathway[]): Pathway[] {
  return JSON.parse(JSON.stringify(pathways)) as Pathway[];
}

/** Reader-facing chip label per candidate technology. `gas-sc` reads as the
 *  reciprocating-engine leg (its economics ARE recip economics). */
const TECH_CHIP_LABEL: Record<TechnologyId, string> = {
  'gas-cc': 'Gas CC',
  'gas-sc': 'Recip Engine',
  'fuel-cell-sofc': 'Fuel Cell (SOFC)',
  'fuel-cell-pafc': 'Fuel Cell (PAFC)',
  'fuel-cell-pem': 'Fuel Cell (PEM H2)',
  'fuel-cell-mcfc': 'Fuel Cell (MCFC)',
  'nuclear-smr': 'SMR',
  'nuclear-large': 'Large Nuclear',
  'solar-bess': 'Solar+BESS',
  'wind-bess': 'Wind+BESS',
  'grid-tariff': 'Grid',
  mixed: 'Hybrid',
};

/**
 * Seed comparison pathways for an explicit technology selection — always with
 * the grid-tariff baseline first, then one single-tech pathway per chosen BTM
 * technology, each sized via its preset factory (the SAME load→nameplate source
 * of truth the picker uses). Drives the feasibility-gated toggle recompute.
 */
function seedSelectedTechnologies(
  selected: TechnologyId[],
  intent: ProjectIntent,
  siteContext: SiteContext,
): Pathway[] {
  // grid-tariff baseline is always present (the prompt: keep grid available).
  const techs: TechnologyId[] = ['grid-tariff', ...selected.filter((t) => t !== 'grid-tariff')];
  const seen = new Set<TechnologyId>();
  const out: Pathway[] = [];
  techs.forEach((tech, idx) => {
    if (seen.has(tech)) return;
    seen.add(tech);
    // Q7 — a technology with no preset factory (`mixed`, since the hybrid
    // bridge was retired) is skipped rather than crashing the comparison. A
    // persisted selection can still name it.
    const factory = PRESET_BY_TECHNOLOGY[tech];
    if (!factory) return;
    const seeded = factory(intent, siteContext);
    out.push({
      ...seeded,
      id: `${seeded.id}_sel_${idx}`,
      name: pathwayDisplayNameWithCod(seeded),
    });
  });
  return out;
}

/**
 * U4 / Stage 3 — build the intent-driven default selection from the project.
 *
 * When the intent carries a phased rollout (`intent.phases?.length`), prefer
 * the FAITHFUL per-phase pathways (`pathwaysFromIntentPhases`) — these reflect
 * the actual phased sources / MW / COD the user entered. Otherwise fall back
 * to the posture HEURISTIC (`recommendedPathwaysForIntent`) which surfaces a
 * curated 2-alternative selection from coarse intent signals.
 *
 * Mirrors PathwayPicker.addPreset seeding so the seeded rows behave identically
 * to picker-added ones (unique id, COD-stamped name).
 */
function seedIntentDefaults(
  intent: ProjectIntent,
  siteContext: SiteContext,
): Pathway[] {
  if (intent.phases?.length) {
    const phasePathways = pathwaysFromIntentPhases(intent, siteContext);
    if (phasePathways.length >= 2) return phasePathways;
  }

  const { defaultSelection } = recommendedPathwaysForIntent(intent);
  return defaultSelection.flatMap((tech, idx) => {
    const factory = PRESET_BY_TECHNOLOGY[tech];
    if (!factory) return []; // Q7 — see the note in the selection builder above.
    const seeded = factory(intent, siteContext);
    return {
      ...seeded,
      // Distinct, stable-within-render ids so React keys + removal work.
      id: `${seeded.id}_intent_${idx}`,
      name: pathwayDisplayNameWithCod(seeded),
    };
  });
}

export function PathwayComparisonSection({
  siteId,
  intent,
  siteContext,
  updatedBy,
  uid,
  financialAssumptions,
  initialComparisonId,
  pinnedPathwayComparisonId,
  onPinnedComparisonChange,
  view = 'all',
}: PathwayComparisonSectionProps) {
  const [pathways, setPathways] = useState<Pathway[]>([]);
  const [savedPathways, setSavedPathways] = useState<SavedSitePathwayDocument[]>([]);
  const [savedPathwaysError, setSavedPathwaysError] = useState<string | null>(null);
  const [comparisonId, setComparisonId] = useState<string | null>(initialComparisonId ?? null);
  const [comparisonName, setComparisonName] = useState('Pathway comparison');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);
  // U4 — true while the current selection was auto-seeded from project intent
  // (not loaded from a saved comparison and not yet user-confirmed). Drives the
  // "Defaults from your project intent" banner + Reset button.
  const [seededFromIntent, setSeededFromIntent] = useState(false);
  // Part B — feasibility-gated BTM-technology toggle. The set of technologies
  // the user has chosen to TEST; empty until the user interacts (the picker /
  // intent defaults still seed the initial comparison). Driving setPathways from
  // here recomputes the compare card + charts.
  const [selectedTechnologies, setSelectedTechnologies] = useState<TechnologyId[]>([]);

  useUrlSearchParamSync('pathwayCompare', comparisonId);

  // Part B — feasibility verdicts per candidate BTM technology. Reuses the
  // report's firm-capacity engine (`feasibleTechnologiesForIntent`) so the
  // toggle only OFFERS technologies whose firm capacity can serve the load
  // (feasible / marginal); infeasible techs are disabled with the engine's
  // message as the tooltip. grid-tariff is the always-available baseline.
  const feasibleVerdicts = useMemo<FeasibleTechnologyVerdict[]>(
    () => feasibleTechnologiesForIntent(intent),
    [intent],
  );

  const loadSavedPathways = useCallback(async () => {
    if (!uid) return;
    try {
      const docs = await listSavedSitePathwaysFromFirestore({ uid, siteId });
      setSavedPathways(docs);
      setSavedPathwaysError(null);
    } catch (err) {
      // E3 — this used to swallow the error entirely. The most common failure
      // was PERMISSION_DENIED on a company-shared site (the collection was
      // keyed by the VIEWER's uid, not the owner's), and the user saw an empty
      // "Saved pathways" list that looked like "you have never saved one"
      // rather than "we could not read them".
      setSavedPathwaysError((err as Error)?.message ?? 'Could not load saved pathways');
    }
  }, [siteId, uid]);

  const loadComparison = useCallback(
    async (id: string) => {
      if (!uid) return;
      setLoading(true);
      try {
        const doc = await loadPathwayComparisonFromFirestore({ uid, siteId, comparisonId: id });
        if (!doc) {
          toast.error('Saved comparison not found');
          return;
        }
        setPathways(clonePathways(doc.pathways));
        setComparisonId(doc.id);
        setComparisonName(doc.name);
        // A loaded comparison is authoritative — drop the intent-seed banner.
        setSeededFromIntent(false);
        toast.success(`Loaded "${doc.name}"`);
      } catch (err) {
        toast.error(`Load failed: ${(err as Error).message}`);
      } finally {
        setLoading(false);
      }
    },
    [siteId, uid],
  );

  useEffect(() => {
    void loadSavedPathways();
  }, [loadSavedPathways]);

  useEffect(() => {
    if (initialComparisonId) {
      void loadComparison(initialComparisonId);
    }
  }, [initialComparisonId, loadComparison]);

  // U4 — seed an intent-driven default selection on first mount when there is
  // nothing to load. Guards:
  //   - no pathways already selected (never clobber a user/loaded selection)
  //   - no initialComparisonId (a saved comparison is being loaded — defer)
  //   - no comparisonId (nothing saved/loaded yet)
  //   - not currently loading
  // The seeded rows are flagged so the banner shows and so this never re-fires
  // once the user edits or a comparison loads (which clears the empty state).
  useEffect(() => {
    if (
      pathways.length === 0 &&
      !initialComparisonId &&
      !comparisonId &&
      !loading
    ) {
      const seeded = seedIntentDefaults(intent, siteContext);
      if (seeded.length >= 2) {
        setPathways(seeded);
        setSeededFromIntent(true);
      }
    }
    // Intentionally not depending on `intent`/`siteContext` to avoid re-seeding
    // on every prop identity change — we only seed the initial empty state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialComparisonId, comparisonId, loading]);

  const handleResetToIntentDefaults = useCallback(() => {
    const seeded = seedIntentDefaults(intent, siteContext);
    if (seeded.length < 2) return;
    setPathways(seeded);
    setComparisonId(null);
    setComparisonName('Pathway comparison');
    setSeededFromIntent(true);
    toast.success('Reset to intent defaults');
  }, [intent, siteContext]);

  // U4 — once the user confirms a selection in the picker, it is no longer an
  // auto-seed; drop the banner so the "Defaults from intent" copy is honest.
  const handlePathwaysChange = useCallback((next: Pathway[]) => {
    setPathways(next);
    setSeededFromIntent(false);
    // A manual picker edit supersedes the feasibility-toggle selection.
    setSelectedTechnologies([]);
  }, []);

  // Part B — toggle a candidate BTM technology in/out of the test set. On
  // change, re-seed the comparison from the new selection (grid baseline + the
  // chosen feasible techs) and recompute. Infeasible techs are never togglable
  // (the chip is disabled), so this only ever receives selectable techs.
  const handleToggleTechnology = useCallback(
    (tech: TechnologyId) => {
      setSelectedTechnologies((prev) => {
        const next = prev.includes(tech)
          ? prev.filter((t) => t !== tech)
          : [...prev, tech];
        const seeded = seedSelectedTechnologies(next, intent, siteContext);
        // Keep ≥ MIN (2) rows so the comparison stays rendered; grid baseline +
        // ≥1 chosen tech satisfies this. Below 2, leave the prior pathways.
        if (seeded.length >= 2) {
          setPathways(seeded);
          setSeededFromIntent(false);
        }
        return next;
      });
    },
    [intent, siteContext],
  );

  const handleSaveComparison = useCallback(async () => {
    if (!uid) {
      toast.error('Sign in to save comparisons');
      return;
    }
    if (pathways.length < 2) {
      toast.error('Select at least 2 pathways before saving');
      return;
    }
    setSaving(true);
    try {
      const doc = await savePathwayComparisonToFirestore({
        uid,
        siteId,
        pathways,
        name: comparisonName.trim() || 'Pathway comparison',
        createdBy: updatedBy,
        comparisonId: comparisonId ?? undefined,
      });
      setComparisonId(doc.id);
      toast.success('Comparison saved');
    } catch (err) {
      toast.error(`Save failed: ${(err as Error).message}`);
    } finally {
      setSaving(false);
    }
  }, [comparisonId, comparisonName, pathways, siteId, uid, updatedBy]);

  const handleSavePathway = useCallback(
    async (pathway: Pathway, label: string) => {
      if (!uid) {
        toast.error('Sign in to save pathways');
        return;
      }
      try {
        await saveSitePathwayToFirestore({
          uid,
          siteId,
          pathway,
          label,
          createdBy: updatedBy,
        });
        await loadSavedPathways();
        toast.success(`Saved "${label}" to this site`);
      } catch (err) {
        toast.error(`Save pathway failed: ${(err as Error).message}`);
      }
    },
    [loadSavedPathways, siteId, uid, updatedBy],
  );

  const handlePinComparison = useCallback(async () => {
    if (!uid || !comparisonId) {
      toast.error('Save the comparison before pinning');
      return;
    }
    try {
      await pinPathwayComparisonForSite({ uid, siteId, comparisonId });
      onPinnedComparisonChange?.(comparisonId);
      toast.success('Pinned for report citation');
    } catch (err) {
      toast.error(`Pin failed: ${(err as Error).message}`);
    }
  }, [comparisonId, onPinnedComparisonChange, siteId, uid]);

  const handleUnpinComparison = useCallback(async () => {
    if (!uid) return;
    try {
      await unpinPathwayComparisonForSite({ uid, siteId });
      onPinnedComparisonChange?.(null);
      toast.success('Unpinned decision artifact');
    } catch (err) {
      toast.error(`Unpin failed: ${(err as Error).message}`);
    }
  }, [onPinnedComparisonChange, siteId, uid]);

  const showResults = pathways.length >= 2;
  const isPinned = Boolean(comparisonId && pinnedPathwayComparisonId === comparisonId);

  const sectionTitle = useMemo(
    () => (showResults ? `${pathways.length} pathways selected` : 'Pathway decision framework'),
    [pathways.length, showResults],
  );

  // aurora-qa-3 (⑪) — which slices to render for the active Financial sub-tab.
  const showPicker = view === 'all' || view === 'pathways';
  const showCompareAndRisk = view === 'all' || view === 'pathways';
  const showCashflowLifecycle = view === 'all' || view === 'cashflow-lifecycle';

  return (
    <div className="space-y-4" data-testid="pathway-comparison-section">
      {showPicker ? (
      <div className="rounded-lg border border-border bg-surface-1 p-4">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-widest text-text-primary">
              {sectionTitle}
            </div>
            <div className="text-[10px] text-text-muted mt-0.5">
              Alternatives are suggested from your project intent — compare BTM, grid, and hybrid
              options side-by-side with Monte Carlo bands.
            </div>
          </div>
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin text-text-muted" />
          ) : null}
        </div>

        {/* SAVE + PIN ROW (audit E2 / decision Q4).
            `handleSaveComparison`, `handlePinComparison` and
            `handleUnpinComparison` were all implemented and NONE was rendered,
            so a comparison could not be created from the UI at all — while the
            report pipeline, report setup, the audit provider, the evidence
            loader and the ?pathwayCompare= deep links all went on consuming a
            pin nobody could produce. The lane is restored rather than retired
            because those five consumers are the point of the feature: a pinned
            comparison is what makes a pathway citable in a report. */}
        {showResults ? (
          <div
            className="flex flex-wrap items-center gap-2 mb-3"
            data-testid="pathway-comparison-save-pin-row"
          >
            <input
              type="text"
              value={comparisonName}
              onChange={(e) => setComparisonName(e.target.value)}
              placeholder="Comparison name"
              aria-label="Comparison name"
              className="flex-1 min-w-[10rem] px-2 py-1 text-[11px] rounded-md border border-border bg-surface-0 text-text-primary"
              data-testid="pathway-comparison-name"
            />
            <button
              type="button"
              onClick={() => void handleSaveComparison()}
              disabled={saving || !uid}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-state-hover disabled:opacity-50"
              data-testid="pathway-comparison-save"
            >
              <Save className="w-3 h-3" aria-hidden />
              {saving ? 'Saving…' : comparisonId ? 'Save changes' : 'Save comparison'}
            </button>
            <button
              type="button"
              onClick={() => void (isPinned ? handleUnpinComparison() : handlePinComparison())}
              disabled={!comparisonId || !uid}
              className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-state-hover disabled:opacity-50"
              data-testid="pathway-comparison-pin"
              // Save first: pinning names a SAVED artifact, and a report cites
              // it by id. Without a saved id there is nothing for the report to
              // resolve, so the button says so rather than failing on click.
              title={comparisonId
                ? (isPinned ? 'Unpin this comparison' : 'Pin so reports can cite this comparison')
                : 'Save the comparison before pinning'}
            >
              {isPinned ? <PinOff className="w-3 h-3" aria-hidden /> : <Pin className="w-3 h-3" aria-hidden />}
              {isPinned ? 'Unpin' : 'Pin for reports'}
            </button>
            {/* 6.1 — `buildPathwayComparisonShareUrl` had no caller because
                nothing could CREATE a comparison to share. With the save lane
                restored the deep link is producible, and the `?pathwayCompare=`
                param the section already reads on mount is what it resolves. */}
            {comparisonId ? (
              <button
                type="button"
                onClick={() => {
                  const url = buildPathwayComparisonShareUrl(siteId, comparisonId);
                  void navigator.clipboard?.writeText(url)
                    .then(() => toast.success('Comparison link copied'))
                    .catch(() => toast.error('Could not copy the link'));
                }}
                className="inline-flex items-center gap-1 px-2 py-1 text-[11px] rounded-md border border-border hover:bg-state-hover"
                data-testid="pathway-comparison-share"
              >
                <Link2 className="w-3 h-3" aria-hidden />
                Copy link
              </button>
            ) : null}
            {isPinned ? (
              <span className="text-[10px] text-text-muted" data-testid="pathway-comparison-pinned-note">
                Reports on this site cite this comparison.
              </span>
            ) : null}
          </div>
        ) : null}

        {savedPathwaysError ? (
          <div
            className="mb-3 px-2 py-1 text-[10px] rounded-md border border-warning/50 bg-warning/10 text-warning"
            data-testid="pathway-saved-load-error"
          >
            Saved pathways could not be loaded: {savedPathwaysError}
          </div>
        ) : null}

        <PathwayPicker
          intent={intent}
          siteContext={siteContext}
          pathways={pathways}
          onChange={handlePathwaysChange}
          savedPathways={savedPathways}
          onSavePathway={handleSavePathway}
          siteId={siteId}
          updatedBy={updatedBy}
          baseFinancialAssumptions={financialAssumptions}
        />

        {feasibleVerdicts.length > 0 ? (
          <div className="mt-3" data-testid="pathway-btm-tech-toggle">
            <div className="text-[10px] font-bold uppercase tracking-widest text-text-muted mb-1.5">
              Test BTM technologies
              <span className="ml-2 normal-case font-normal tracking-normal">
                (only technologies whose firm capacity can serve your load are selectable)
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {feasibleVerdicts.map((v) => {
                const active = selectedTechnologies.includes(v.technology);
                const disabled = !v.selectable;
                return (
                  <button
                    key={v.technology}
                    type="button"
                    disabled={disabled}
                    title={v.message}
                    aria-pressed={active}
                    onClick={() => handleToggleTechnology(v.technology)}
                    data-testid={`pathway-btm-tech-chip-${v.technology}`}
                    data-verdict={v.verdict}
                    className={[
                      'px-2.5 py-1 text-xs rounded-full border transition-colors',
                      disabled
                        ? 'border-border text-text-muted opacity-50 cursor-not-allowed'
                        : active
                          ? 'selected-accent'
                          : 'border-border text-text-secondary hover:border-primary/60',
                    ].join(' ')}
                  >
                    {TECH_CHIP_LABEL[v.technology]}
                    {v.verdict === 'marginal' ? ' ·marginal' : ''}
                  </button>
                );
              })}
            </div>
            {selectedTechnologies.length > 0 ? (
              <div className="text-[10px] text-text-muted mt-1.5">
                Comparing grid baseline +{' '}
                {selectedTechnologies.map((t) => TECH_CHIP_LABEL[t]).join(', ')}.
              </div>
            ) : null}
          </div>
        ) : null}

        {/* aurora-qa-3 (⑩) — removed (William): the "Defaults from your project
            intent — edit to compare" banner + "Reset to intent defaults" action,
            AND the comparison name input + "Save comparison" / pin row. The
            named-save feature is intentionally dropped; the comparison cards /
            charts below remain. */}
      </div>
      ) : null}

      {showResults && showCompareAndRisk ? (
        <PathwayCompareCard
          pathways={pathways}
          carbonCommitment={intent?.carbonCommitment}
        />
      ) : null}

      {showResults && showCashflowLifecycle ? (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4" data-testid="pathway-cashflow-lifecycle">
          <PathwayCashflowChart pathways={pathways} />
          <PathwayLifecycleTimeline pathways={pathways} />
        </div>
      ) : null}

      {showResults && showCompareAndRisk ? (
        <PathwayRiskMatrix pathways={pathways} />
      ) : null}
    </div>
  );
}

export type { PathwayComparisonDocument };
